import type { TaskModifier } from './taskSettingsModifiersLib';

/**
 * The filter vocabulary for org-minted task modifiers, shared by Renovator and kaiPro.
 *
 * Both apps let a user filter a scope down to the tasks carrying one modifier, and both hold their
 * other filters in a closed union — Renovator's `ScopeAttentionFilterType`, kaiPro's
 * `TaskFilterMode`. Neither union can name an org's Display modifiers, because an org mints those
 * after the code ships (KAIDEV-3182). So a modifier filter is addressed by the tag it matches,
 * behind a prefix that cannot collide with either union's members.
 *
 * Keeping the unions closed is deliberate, not an accident of history. Renovator's
 * `IS_ATTENTION_FLAG_TYPE` is a Record over its union precisely so TypeScript forces a decision
 * when a new attention condition appears; widening the union to accept any tag would make that
 * Record unsatisfiable and silently drop the check. An org modifier is also a different kind of
 * thing — configuration an org adds and removes, not a condition the product knows how to detect.
 *
 * What is *not* here is either app's set of legacy aliases. Renovator's pre-modifier filters were
 * named `tenantResp` and `deferredTasks`; kaiPro's were `tenantResponsibility` and `deferred`. The
 * names differ, so each app owns its own map and passes it to {@link toCanonicalModifierFilterKey};
 * only the rewrite rule is shared.
 */
export const MODIFIER_FILTER_PREFIX = 'modifier:';

export type ModifierFilterKey = `${typeof MODIFIER_FILTER_PREFIX}${string}`;

export const toModifierFilterKey = (lineItemTag: string): ModifierFilterKey => `${MODIFIER_FILTER_PREFIX}${lineItemTag}`;

export const isModifierFilterKey = (value: string): value is ModifierFilterKey => value.startsWith(MODIFIER_FILTER_PREFIX);

export const getModifierFilterTag = (key: ModifierFilterKey): string => key.slice(MODIFIER_FILTER_PREFIX.length);

/**
 * A modifier filter key that actually names a tag.
 *
 * The bare prefix satisfies the template-literal type but matches nothing, so anything parsing a
 * URL or route param wants this rather than {@link isModifierFilterKey}.
 */
export const isValidModifierFilterKey = (value: string): value is ModifierFilterKey =>
  isModifierFilterKey(value) && value.length > MODIFIER_FILTER_PREFIX.length;

/**
 * Rewrites an app's pre-modifier filter key to the modifier key that now stands for it.
 *
 * A link saved before KAIDEV-4206 carries the old key. It still has to filter — and it still has to
 * read as *selected*, which it would not if the dropdown's rows were keyed by tag while the route
 * said something else. Each app supplies its own map because the old names were never the same
 * between them; see the note on {@link MODIFIER_FILTER_PREFIX}.
 */
export const toCanonicalModifierFilterKey = <Key extends string>(
  key: Key | ModifierFilterKey,
  modifierTagByLegacyKey: Partial<Record<Key, string>>,
): Key | ModifierFilterKey => {
  if (isModifierFilterKey(key)) return key;
  const legacyTag = modifierTagByLegacyKey[key as Key];
  return legacyTag ? toModifierFilterKey(legacyTag) : key;
};

/** The filter keys for a resolved modifier list, in the org's own order. */
export const toModifierFilterKeys = (
  knownTaskModifiers: readonly TaskModifier[],
): ModifierFilterKey[] => knownTaskModifiers.map(modifier => toModifierFilterKey(modifier.lineItemTag));

/**
 * The org's name for the modifier a filter key addresses.
 *
 * Undefined when no configured modifier claims the tag — a saved link to a modifier the org has
 * since deleted. Callers decide whether that is a row to hide or a title to fall back on.
 */
export const getModifierFilterDisplayName = (
  key: ModifierFilterKey,
  knownTaskModifiers: readonly TaskModifier[],
): string | undefined => {
  const tag = getModifierFilterTag(key);
  return knownTaskModifiers.find(modifier => modifier.lineItemTag === tag)?.displayName;
};
